import { Button, Stack, Typography } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  useLazyGetBuyerRequestQuery,
  useSignBuyerRequestMutation,
  useSubmitInvoiceMutation,
  type BuyerRequestSignatureResponse,
} from "api/api-marketplace";
import { getApiErrorMessage } from "helpers/apiErrorMessage";
import { signAndSubmitBuyerRequest } from "helpers/buyerRequestSubmission";
import LetterOfConfirmation from "components/Invoices/AddInvoice/LetterOfConfirmation";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import {
  BUYER_REQUEST_ERROR,
  createBuyerRequestSignature,
  populateBuyerRequestSignature,
  type BuyerRequestSignature,
} from "helpers/buyerRequestSignature";

type BuyerRequestInvoice = {
  invoice_id?: string;
  id?: string;
  invoice_reference_number?: string;
  internal_name?: string;
};

type BuyerRequestToDoProps = {
  invoices: BuyerRequestInvoice[];
};

const BuyerRequestToDo = ({ invoices }: BuyerRequestToDoProps) => {
  const invoice = invoices[0];
  const invoiceId = invoice?.invoice_id ?? invoice?.id;
  const invoiceReference =
    invoice?.invoice_reference_number ??
    invoice?.internal_name ??
    invoiceId ??
    "";
  const [isOpen, setIsOpen] = useState(false);
  const { data: user } = useGetCurrentUserQuery();
  const [signature, setSignature] = useState<BuyerRequestSignature | null>(null);
  const [body, setBody] = useState("");
  const approvalInProgress = useRef(false);
  const signedRequest = useRef<{
    invoiceId: string;
    response: BuyerRequestSignatureResponse;
  } | null>(null);
  const [loadBuyerRequest, { isFetching }] =
    useLazyGetBuyerRequestQuery();
  const [signBuyerRequest, { isLoading: isSigning }] =
    useSignBuyerRequestMutation();
  const [submitInvoice, { isLoading: isSubmitting }] =
    useSubmitInvoiceMutation();

  const handleOpen = useCallback(async () => {
    if (!invoiceId || approvalInProgress.current) return;
    if (signedRequest.current?.invoiceId === invoiceId && body) {
      setIsOpen(true);
      return;
    }

    try {
      const currentSignature = createBuyerRequestSignature(user?.first_name, user?.last_name);
      const template = await loadBuyerRequest(invoiceId).unwrap();
      setBody(populateBuyerRequestSignature(template, currentSignature));
      setSignature(currentSignature);
      setIsOpen(true);
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          BUYER_REQUEST_ERROR.LOAD_FAILED,
        ),
      );
    }
  }, [invoiceId, body, loadBuyerRequest, user]);

  const handleSignAndSubmit = useCallback(async () => {
    if (!invoiceId || !signature || approvalInProgress.current) return;
    approvalInProgress.current = true;

    try {
      await signAndSubmitBuyerRequest(
        async () => {
          if (signedRequest.current?.invoiceId === invoiceId) {
            return signedRequest.current.response;
          }
          const response = await signBuyerRequest({ invoice_id: invoiceId, ...signature }).unwrap();
          signedRequest.current = { invoiceId, response };
          return response;
        },
        () => submitInvoice(invoiceId).unwrap(),
      );
      signedRequest.current = null;
      setIsOpen(false);
      toast.success("Buyer request approved successfully");
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          "Unable to sign the buyer request or submit the invoice.",
        ),
      );
    } finally {
      approvalInProgress.current = false;
    }
  }, [invoiceId, signature, signBuyerRequest, submitInvoice]);

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Typography variant="body2">{invoiceReference}</Typography>
        <Button
          variant="contained"
          size="small"
          onClick={handleOpen}
          disabled={!invoiceId || isFetching || isSigning || isSubmitting || !user}
          sx={{ textTransform: "none" }}
        >
          Open Buyer Request
        </Button>
      </Stack>
      {body && (
        <LetterOfConfirmation
          body={body}
          open={isOpen}
          isLoading={isSigning || isSubmitting}
          onClose={() => {
            if (!approvalInProgress.current) setIsOpen(false);
          }}
          action={handleSignAndSubmit}
        />
      )}
    </>
  );
};

export default BuyerRequestToDo;
